import { getRagStatus } from '@/components/products/MarginBadge';
import { RAGStatus } from '@/components/machines/RAGBadge';

export const PLANOGRAM_UNASSIGNED_KEY = '__unassigned__';

const RAG_SORT_RANK: Record<RAGStatus, number> = {
  RED: 0,
  AMBER: 1,
  GREEN: 2,
};

export function formatPlanogramSlotLabel(
  slotLabel: string | null,
  slotNumber: number
): string {
  return slotLabel?.trim() || `#${slotNumber}`;
}

function lineMarginPercent(cost: number, sellPrice: number): number | null {
  if (sellPrice <= 0 || cost <= 0) return null;
  return Math.round(((sellPrice - cost) / sellPrice) * 100 * 100) / 100;
}

export function resolvePlanogramMarginPercent(slot: PlanogramDisplaySortSlot): number | null {
  if (slot.marginPercent !== undefined) return slot.marginPercent;
  const cost = slot.cost;
  const sell = slot.effectiveSell;
  if (cost != null && sell != null && cost > 0 && sell > 0) {
    return lineMarginPercent(cost, sell);
  }
  return null;
}

export function planogramMarginRagSortRank(marginPercent: number | null): number {
  if (marginPercent == null) return RAG_SORT_RANK.AMBER;
  const status = getRagStatus(marginPercent);
  return RAG_SORT_RANK[status === 'green' ? 'GREEN' : status === 'amber' ? 'AMBER' : 'RED'];
}

export interface PlanogramDisplaySortSlot {
  slotNumber: number;
  slotLabel: string | null;
  productId: string | null;
  productName?: string | null;
  marginPercent?: number | null;
  cost?: number | null;
  effectiveSell?: number | null;
}

export function earliestPlanogramSlotNumber(
  slots: Array<{ slotNumber: number }>
): number {
  if (slots.length === 0) return Number.POSITIVE_INFINITY;
  return Math.min(...slots.map((slot) => slot.slotNumber));
}

export function comparePlanogramGroupsBySlotNumber(
  a: { earliestSlotNumber: number; empty: boolean },
  b: { earliestSlotNumber: number; empty: boolean }
): number {
  if (a.empty !== b.empty) return a.empty ? 1 : -1;
  return a.earliestSlotNumber - b.earliestSlotNumber;
}

/**
 * Flat sort used by planogram builder list and income projections:
 * product group by earliest slot number → slot number within group.
 * Empty slots are grouped last.
 */
export function sortPlanogramDisplaySlots<T extends PlanogramDisplaySortSlot>(slots: T[]): T[] {
  const groups = new Map<string, T[]>();

  for (const slot of slots) {
    const key = slot.productId ?? PLANOGRAM_UNASSIGNED_KEY;
    const group = groups.get(key);
    if (group) group.push(slot);
    else groups.set(key, [slot]);
  }

  const sortedGroups = Array.from(groups.entries()).map(([key, groupSlots]) => {
    const sorted = [...groupSlots].sort((a, b) => a.slotNumber - b.slotNumber);
    return {
      sorted,
      empty: key === PLANOGRAM_UNASSIGNED_KEY,
      earliestSlotNumber: earliestPlanogramSlotNumber(sorted),
    };
  });

  sortedGroups.sort(comparePlanogramGroupsBySlotNumber);

  return sortedGroups.flatMap((group) => group.sorted);
}
